'use client';
/*
 * @providesModule SkillzImage
 */
import React, {useState, useEffect} from 'react';
//@ts-ignore
import {source_svgs} from '@sdk-mobile';
import source_images from './source_images.generated';
import {Image, ImageURISource, ImageProps} from 'react-native';
import {useSkillzStyle} from '@/app/(app)/games/[gid]/_src/SkillzStyleProvider';

type SkillzImageProps = ImageProps & {color?: string; tintColor?: string} & {
  source: string | ImageURISource;
};

/**
 * `require('./x.png')` / `require('./x.svg')` results are not one shape: depending on
 * the loader Next hands back a StaticImageData ({src}), a plain URL string, or either
 * of those behind `.default`. Reading `.src` blindly yields `undefined` and the image
 * silently renders nothing. Normalise every shape to a usable URL.
 */
function resolveBundledAsset(entry: unknown): string | null {
  if (!entry) return null;
  if (typeof entry === 'string') return entry;
  const obj = entry as {src?: unknown; default?: unknown};
  if (typeof obj.src === 'string') return obj.src;
  if (typeof obj.default === 'string') return obj.default;
  const inner = obj.default as {src?: unknown} | undefined;
  if (inner && typeof inner.src === 'string') return inner.src;
  return null;
}

function useResolvedSource(source: string) {
  const {themeImagesUrl, isThemeImage, sourceBaseUrl, isLocalImage, hasLoaded} = useSkillzStyle();
  const [resolvedSource, setResolvedSource] = useState<string | null>(null);

  useEffect(() => {
    // Validate that source is not empty to prevent directory path requests.
    if (!source || source.trim() === '' || source === '/') {
      setResolvedSource(null);
      return;
    }

    // These sources do NOT depend on the loaded theme, so resolve them immediately —
    // gating them on `hasLoaded` (which is never true outside a SkillzSettingsProvider
    // subtree, e.g. the multicade StoreV2 surface) hides bundled assets like the
    // deposit-tile BEST ribbon, bonus tags and supported-payments strip. (RP-10858)
    if (
      source.startsWith('https://') ||
      source.startsWith('/_next/static') ||
      source.startsWith('data:image/')
    ) {
      setResolvedSource(source);
      return;
    }

    // Bundled SVG map.
    const svgUrl = resolveBundledAsset(source_svgs[source]);
    if (svgUrl) {
      setResolvedSource(svgUrl);
      return;
    }

    // A loaded theme may override a bundled raster, so the theme lookup still wins —
    // but only when there actually is a loaded theme to ask.
    if (hasLoaded && isThemeImage(source)) {
      setResolvedSource(themeImagesUrl + source);
      return;
    }

    // Bundled raster map (PNG/JPG from sdk-core's source_images). Like the SVG map this
    // does NOT depend on the theme, so it must not be gated on `hasLoaded` — that flag
    // is never true outside a SkillzSettingsProvider subtree (e.g. the multicade StoreV2
    // surface), which is what left every PNG rendering nothing. (RP-10858)
    const rasterUrl = resolveBundledAsset(source_images[source]);
    if (rasterUrl) {
      setResolvedSource(rasterUrl);
      return;
    }

    // Theme-dependent resolution waits for the style bundle to load.
    if (!hasLoaded) return;

    // `localImages` (sdk-mobile-images.json) now also lists the bundled rasters, which
    // are NOT served from /public. Anything the raster map knows about was already
    // resolved above; if normalising its entry somehow failed, fall through to the CDN
    // rather than request a /public path that does not exist.
    if (!source_images[source] && isLocalImage(source)) {
      setResolvedSource(`/${source}`);
      return;
    }

    setResolvedSource(sourceBaseUrl + source);
  }, [isLocalImage, isThemeImage, source, sourceBaseUrl, themeImagesUrl, hasLoaded]);

  return resolvedSource;
}

export default function SkillzImage(props: SkillzImageProps) {
  const source = typeof props.source === 'string' ? props.source : (props?.source?.uri ?? '');
  const resolvedSource = useResolvedSource(source);
  if (!resolvedSource) return null;
  const {color, tintColor} = props;
  return (
    <Image
      {...props}
      source={{uri: resolvedSource}}
      alt='skillz image'
      tintColor={tintColor || color}
    />
  );
}
